import React, { useState, useEffect } from 'react';
import { Table, Button, Drawer, List, Tag, Card, Typography, Badge, Empty, message, Spin } from 'antd';
import { CalendarOutlined, UserOutlined, ClockCircleOutlined, TeamOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import constants from '../../constants';

const { Title, Text } = Typography;

const DoubtSessionsComponent = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // Function to fetch data from API
  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constants.NEW_DOMAIN}/api/v1/meeting/getUpcomingDoubtSession`);
      if (response.data.success) {
        setSessions(response.data.data);
      } else {
        message.error('Failed to load sessions data');
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      message.error('Error fetching sessions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Function to get category color based on category code
  const getCategoryColor = (categoryCode) => {
    const colorMap = {
      CW: 'blue',
      FMPRO: 'purple',
      GD: 'green',
      PSY: 'orange',
      SMM: 'cyan',
      VE: 'red',
      WD: 'geekblue'
    };
    return colorMap[categoryCode] || 'default';
  };

  // Function to format date and time
  const formatDateTime = (dateTimeString) => {
    return moment(dateTimeString).format('MMM DD, YYYY - hh:mm A');
  };

  // Function to handle view students button click
  const handleViewStudents = (session) => {
    setSelectedSession(session);
    setDrawerVisible(true);
  };

  // Define table columns
  const columns = [
    {
      title: 'Session ID',
      dataIndex: 'slotId',
      key: 'slotId',
    },
    {
      title: 'Category',
      dataIndex: 'categoryCode',
      key: 'categoryCode',
      render: (categoryCode) => (
        <Tag color={getCategoryColor(categoryCode)}>{categoryCode}</Tag>
      ),
      filters: [
        { text: 'CW', value: 'CW' },
        { text: 'FMPRO', value: 'FMPRO' },
        { text: 'GD', value: 'GD' },
        { text: 'PSY', value: 'PSY' },
        { text: 'SMM', value: 'SMM' },
        { text: 'VE', value: 'VE' },
        { text: 'WD', value: 'WD' },
      ],
      onFilter: (value, record) => record.categoryCode === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (startTime) => (
        <span><ClockCircleOutlined /> {formatDateTime(startTime)}</span>
      ),
      sorter: (a, b) => new Date(a.startTime) - new Date(b.startTime),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (endTime) => (
        <span><ClockCircleOutlined /> {formatDateTime(endTime)}</span>
      ),
    },
    {
      title: 'Enrolled Students',
      dataIndex: 'enrolledStudents',
      key: 'enrolledStudents',
      render: (enrolledStudents) => (
        <Badge count={enrolledStudents.length} style={{ backgroundColor: enrolledStudents.length > 0 ? '#52c41a' : '#d9d9d9' }} />
      ),
      sorter: (a, b) => a.enrolledStudents.length - b.enrolledStudents.length,
    },
    {
      title: 'Available Slots',
      dataIndex: 'remainingEnrollment',
      key: 'remainingEnrollment',
      sorter: (a, b) => a.remainingEnrollment - b.remainingEnrollment,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          onClick={() => handleViewStudents(record)}
          icon={<TeamOutlined />}
          disabled={record.enrolledStudents.length === 0}
        >
          View Students
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <CalendarOutlined /> Upcoming Doubt Sessions
            </Title>
            <Text type="secondary">
              Scheduled doubt sessions for April 19, 2025
            </Text>
          </div>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchSessions} 
            loading={loading}
          >
            Refresh
          </Button>
        </div>
        
        <Spin spinning={loading}>
          <Table 
            dataSource={sessions}
            columns={columns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            style={{ marginTop: '20px' }}
          />
        </Spin>
      </Card>

      <Drawer
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Enrolled Students</Title>
            {selectedSession && (
              <div style={{ marginTop: '8px' }}>
                <Tag color={getCategoryColor(selectedSession.categoryCode)}>
                  {selectedSession.categoryCode}
                </Tag>
                <Text>{selectedSession.description}</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text type="secondary">
                    {formatDateTime(selectedSession.startTime)} to {formatDateTime(selectedSession.endTime)}
                  </Text>
                </div>
              </div>
            )}
          </div>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={400}
      >
        {selectedSession && selectedSession.enrolledStudents.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={selectedSession.enrolledStudents}
            renderItem={(student, index) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <UserOutlined style={{ fontSize: '24px', color: '#1890ff', backgroundColor: '#e6f7ff', padding: '8px', borderRadius: '50%' }} />
                  }
                  title={student.email}
                  description={
                    <div>
                      <div>
                        <Text strong>Phone:</Text> {student.phone}
                      </div>
                      <div>
                        <Text strong>Enrollment ID:</Text> {student.enrollmentId}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty 
            description="No students enrolled yet" 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        )}
      </Drawer>
    </div>
  );
};

export default DoubtSessionsComponent;